// =============================================================================
// AuthService — handles login / register / refresh / logout, persists tokens,
// runs an auto-refresh timer, and exposes a TokenProvider for ApiClient.
//
// Tokens stored in localStorage (`digimon:auth:v1`). For higher-security games
// swap to a secure cookie + same-origin proxy; the surface is unchanged.
// =============================================================================

import type { ApiClient, TokenProvider } from "./ApiClient";
import type { AuthResponse } from "@game/types";

const STORAGE_KEY = "digimon:auth:v1";
/** Refresh ~2 min before access token expiry (default 15 min). */
const REFRESH_LEAD_MS = 2 * 60 * 1000;
/** Conservative re-fire if we can't decode the token. */
const FALLBACK_REFRESH_MS = 12 * 60 * 1000;

interface StoredAuth {
  token: string;
  refreshToken: string;
  accountId: string;
  characterId: string;
  characterName: string;
  emailVerified: boolean;
  /** ms epoch when the access token expires (parsed from JWT exp), or 0 if unknown. */
  expiresAt: number;
}

type Listener = (auth: StoredAuth | null) => void;

export class AuthService {
  private auth: StoredAuth | null = null;
  private listeners = new Set<Listener>();
  private refreshTimer: number | undefined;
  private inFlightRefresh: Promise<string | null> | null = null;

  constructor(private readonly api: ApiClient) {
    this.auth = loadFromStorage();
    if (this.auth) this.scheduleRefresh();
  }

  // ---- TokenProvider for ApiClient ------------------------------------

  asTokenProvider(onAuthFailed?: () => void): TokenProvider {
    return {
      getToken: () => this.auth?.token ?? null,
      refresh: () => this.refresh(),
      ...(onAuthFailed ? { onAuthFailed } : {}),
    };
  }

  // ---- public state ---------------------------------------------------

  current(): StoredAuth | null {
    return this.auth;
  }

  isAuthenticated(): boolean {
    return this.auth !== null;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  // ---- auth flows -----------------------------------------------------

  async login(email: string, password: string): Promise<StoredAuth> {
    const r = await this.api.post<AuthResponse>("/auth/login", { email, password }, { noAuth: true });
    const stored = this.persist(r);
    this.scheduleRefresh();
    return stored;
  }

  async register(email: string, password: string, characterName: string): Promise<StoredAuth> {
    const r = await this.api.post<AuthResponse>(
      "/auth/register",
      { email, password, characterName },
      { noAuth: true },
    );
    const stored = this.persist(r);
    this.scheduleRefresh();
    return stored;
  }

  async refresh(): Promise<string | null> {
    if (this.inFlightRefresh) return this.inFlightRefresh;
    const cur = this.auth;
    if (!cur?.refreshToken) return null;
    this.inFlightRefresh = (async () => {
      try {
        const r = await this.api.post<AuthResponse>(
          "/auth/refresh",
          { refreshToken: cur.refreshToken },
          { noAuth: true },
        );
        const stored = this.persist(r);
        this.scheduleRefresh();
        return stored.token;
      } catch (err) {
        // Refresh failed — clear stored tokens.
        this.clearAuth();
        return null;
      } finally {
        this.inFlightRefresh = null;
      }
    })();
    return this.inFlightRefresh;
  }

  async logout(allDevices = false): Promise<void> {
    const cur = this.auth;
    if (!cur) return;
    try {
      const body = allDevices ? {} : { refreshToken: cur.refreshToken };
      await this.api.post<unknown>("/auth/logout", body);
    } catch {
      // ignore — we'll clear locally regardless
    }
    this.clearAuth();
  }

  // ---- helpers --------------------------------------------------------

  async forgotPassword(email: string): Promise<void> {
    await this.api.post<unknown>("/auth/forgot-password", { email }, { noAuth: true });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.api.post<unknown>("/auth/reset-password", { token, newPassword }, { noAuth: true });
  }

  async verifyEmail(token: string): Promise<void> {
    await this.api.post<unknown>("/auth/verify-email", { token }, { noAuth: true });
    if (this.auth) {
      this.auth = { ...this.auth, emailVerified: true };
      this.persistRaw(this.auth);
      this.notify();
    }
  }

  async resendVerification(): Promise<void> {
    await this.api.post<unknown>("/auth/resend-verification", undefined);
  }

  // ---- internal -------------------------------------------------------

  private persist(r: AuthResponse): StoredAuth {
    const expiresAt = parseJwtExp(r.token);
    const stored: StoredAuth = {
      token: r.token,
      refreshToken: r.refreshToken,
      accountId: r.accountId,
      characterId: r.characterId,
      characterName: r.characterName,
      emailVerified: r.emailVerified,
      expiresAt,
    };
    this.persistRaw(stored);
    this.notify();
    return stored;
  }

  private persistRaw(s: StoredAuth): void {
    this.auth = s;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // ignore — private mode etc.
    }
  }

  private clearAuth(): void {
    this.auth = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    if (this.refreshTimer !== undefined) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    this.notify();
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer !== undefined) clearTimeout(this.refreshTimer);
    if (!this.auth) return;
    const now = Date.now();
    const exp = this.auth.expiresAt;
    let delay: number;
    if (exp > 0) {
      delay = Math.max(5000, exp - now - REFRESH_LEAD_MS);
    } else {
      delay = FALLBACK_REFRESH_MS;
    }
    this.refreshTimer = window.setTimeout(() => {
      void this.refresh();
    }, delay);
  }

  private notify(): void {
    for (const fn of this.listeners) fn(this.auth);
  }
}

// ---- helpers ----------------------------------------------------------

function loadFromStorage(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

/** Pull `exp` (seconds since epoch) out of a JWT body without verifying. */
function parseJwtExp(token: string): number {
  try {
    const [, payload] = token.split(".");
    if (!payload) return 0;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const data = JSON.parse(json) as { exp?: number };
    if (typeof data.exp === "number") return data.exp * 1000;
  } catch {
    // ignore
  }
  return 0;
}
